import { ComponentType } from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class SideDialogService {
  public static readonly defaultSideDialogConfig: MatDialogConfig = {
    hasBackdrop: true,
    height: 'calc(100% - 32px)',
    maxWidth: '100%',
    panelClass: 'side-dialog-panel',
    position: {
      bottom: '16px',
      right: '16px',
      top: '16px',
    },
    width: '1232px',
  };

  public static readonly openAnimationDuration = 200;
  public static readonly closeAnimationDuration = 150;
  public static readonly animationDelay = 100;

  private readonly matDialog = inject(MatDialog);

  private openRefs: MatDialogRef<unknown>[] = [];

  public open<T>(component: ComponentType<T>, config?: MatDialogConfig): MatDialogRef<T> {
    const isNested = this.openRefs.length > 0;

    const basePanelClass = config?.panelClass ?? SideDialogService.defaultSideDialogConfig.panelClass;
    const normalizedPanelClasses: string[] = Array.isArray(basePanelClass)
      ? [...basePanelClass]
      : typeof basePanelClass === 'string'
        ? basePanelClass.split(/\s+/).filter(Boolean)
        : [];

    if (isNested && !normalizedPanelClasses.includes('sd-nested')) {
      normalizedPanelClasses.push('sd-nested');
    }

    const mergedConfig: MatDialogConfig = {
      ...SideDialogService.defaultSideDialogConfig,
      autoFocus: config?.autoFocus ?? true,
      hasBackdrop: config?.hasBackdrop ?? true,
      restoreFocus: config?.restoreFocus ?? true,
      ...config,
      panelClass: normalizedPanelClasses,
    };

    const dialogRef = this.matDialog.open(component, mergedConfig);

    this.openRefs.push(dialogRef as unknown as MatDialogRef<unknown>);
    dialogRef.afterClosed().subscribe(() => {
      this.openRefs = this.openRefs.filter((ref) => ref !== (dialogRef as unknown as MatDialogRef<unknown>));
    });

    const containers = Array.from(document.getElementsByTagName('mat-dialog-container')) as HTMLElement[];
    const container = containers[containers.length - 1];
    const wrapper = container?.closest('.cdk-global-overlay-wrapper') as HTMLElement | null;

    if (container && wrapper) {
      this.applyNestedSlideIn(wrapper, container);
      this.wrapCloseWithSlideOut(dialogRef, wrapper, container);
    }

    return dialogRef;
  }

  public close<T>(dialogRef: MatDialogRef<T>): void {
    dialogRef.close();
  }

  private applyNestedSlideIn(wrapper: HTMLElement, container: HTMLElement): void {
    const keyFrameZero: Record<string, string> = {};
    const keyFrameEnd: Record<string, string> = {};
    const toProp = 'left';
    keyFrameZero[toProp] = '0px';
    keyFrameEnd[toProp] = `${container.clientWidth}px`;

    wrapper.animate([keyFrameEnd, keyFrameZero], {
      duration: SideDialogService.openAnimationDuration,
      easing: 'ease-in-out',
    });
  }

  private wrapCloseWithSlideOut<T>(dialogRef: MatDialogRef<T>, wrapper: HTMLElement, container: HTMLElement): void {
    const keyFrameZero: Record<string, string> = {};
    const keyFrameEnd: Record<string, string> = {};
    const toProp = 'left';
    keyFrameZero[toProp] = '0px';
    keyFrameEnd[toProp] = `${container.clientWidth}px`;

    const originalClose = dialogRef.close.bind(dialogRef);
    (dialogRef as unknown as { close: (result?: unknown) => void }).close = (result?: unknown): void => {
      const outgoing: Animation = wrapper.animate([keyFrameZero, keyFrameEnd], {
        duration: SideDialogService.closeAnimationDuration,
        easing: 'ease-in-out',
      });

      outgoing.onfinish = (): void => {
        wrapper.style.display = 'none';
        originalClose(result as unknown);
      };
    };
  }
}
